// Title-bar user chip. Shows the signed-in user's avatar + name,
// click to open a tiny menu with sign-out.
//
// Adapted from agicore-foundry's UserChip — restyled into BKA's
// warm-publication register and with the GithubBackupModal item
// dropped (no git layer until Sprint 1). Sign-out is the only menu
// action in the skeleton.

import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../store/authStore";
import { googleSignOut } from "../lib/google-auth";

export function UserChip() {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user) return null;

  const handleSignOut = () => {
    googleSignOut();
    signOut();
    setOpen(false);
  };

  // Prefer first name in the chip to keep the title bar compact; the
  // full name + email show in the open menu.
  const short = user.name.split(/\s+/)[0] || user.email;

  return (
    <div className="user-chip" ref={rootRef}>
      <button type="button" className="user-chip-btn" onClick={() => setOpen((o) => !o)} title={user.email}>
        {user.picture
          ? <img src={user.picture} alt="" className="user-chip-pic" referrerPolicy="no-referrer" />
          : <span className="user-chip-initial">{(user.name[0] ?? user.email[0] ?? "?").toUpperCase()}</span>}
        <span className="user-chip-name">{short}</span>
      </button>
      {open && (
        <div className="user-menu">
          <div className="user-menu-head">
            <div className="user-menu-name">{user.name}</div>
            <div className="user-menu-email">{user.email}</div>
          </div>
          <button type="button" className="user-menu-item" onClick={handleSignOut}>
            sign out
          </button>
        </div>
      )}
      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .user-chip { position: relative; display: inline-block; }
      .user-chip-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 2px 10px 2px 2px;
        background: var(--paper-raised);
        border: 1px solid var(--line);
        border-radius: 14px;
        color: var(--ink);
        font-family: var(--font-sans);
        font-size: 12px;
        cursor: pointer;
      }
      .user-chip-btn:hover { border-color: var(--teal); color: var(--teal); }
      .user-chip-pic {
        width: 22px; height: 22px;
        border-radius: 50%;
        object-fit: cover;
      }
      .user-chip-initial {
        width: 22px; height: 22px;
        border-radius: 50%;
        background: var(--teal);
        color: var(--paper-raised);
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: 600;
      }
      .user-chip-name {
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .user-menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        min-width: 220px;
        background: var(--paper-raised);
        border: 1px solid var(--line);
        border-radius: var(--radius-1);
        box-shadow: var(--shadow-2);
        z-index: 100;
        padding: var(--space-1);
      }
      .user-menu-head {
        padding: var(--space-2) var(--space-3);
        border-bottom: 1px solid var(--line);
        margin-bottom: var(--space-1);
      }
      .user-menu-name {
        font-size: 13px;
        color: var(--ink);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .user-menu-email {
        font-family: var(--font-mono);
        font-size: 10px;
        color: var(--ink-faint);
        margin-top: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .user-menu-item {
        width: 100%;
        text-align: left;
        font-family: var(--font-sans);
        font-size: 12px;
        padding: 7px var(--space-3);
        background: transparent;
        border: none;
        color: var(--ink-soft);
        border-radius: var(--radius-1);
        cursor: pointer;
      }
      .user-menu-item:hover {
        background: var(--rust-bg);
        color: var(--rust);
      }
    `}</style>
  );
}
